import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ApiError, api } from "../lib/api";

export type ImagerySlotName = "aerial" | "flood";
export type ImageryImportTarget = "aerial" | "flood" | "photos";
export type ImageryOptionKind = "aerial-close" | "aerial-wide" | "topo" | "flood";

export interface ImagerySlotData {
  key: string;
  url: string;
}

export interface ImagerySlots {
  aerial: ImagerySlotData | null;
  flood: ImagerySlotData | null;
}

export interface ImageryOption {
  kind: ImageryOptionKind;
  label: string;
  url: string;
  attribution: string;
}

export interface ImageryOptionsResult {
  options: ImageryOption[];
  floodAvailable: boolean;
  region: string;
}

export interface MovePhoto {
  id: string;
  caption: string | null;
  sortOrder: number;
  url: string;
}

export interface MoveImageResult {
  aerialUrl: string | null;
  floodUrl: string | null;
  photos: MovePhoto[];
}

export type ImageRef = { photoId: string } | { slot: ImagerySlotName };

export interface MoveImagePayload {
  from: ImageRef;
  to: { slot?: ImagerySlotName; target?: "photos" };
}

function slotsQueryKey(recordId: string | undefined): (string | undefined)[] {
  return ["insurance", recordId, "images", "slots"];
}

const COORD_PRECISION = 5;
const IMAGERY_OPTIONS_STALE_MS = 60 * 60 * 1000;

function roundCoord(value: number): number {
  return Number(value.toFixed(COORD_PRECISION));
}

function imageryOptionsByCoordsQueryKey(lat: number | null, lng: number | null): (string | number | null)[] {
  return ["imagery-options", lat, lng];
}

async function uploadSlotFile(recordId: string, slot: ImagerySlotName, file: File): Promise<ImagerySlotData> {
  const formData = new FormData();
  formData.append("file", file);

  const res = await fetch(`/api/insurance/${recordId}/images/slot/${slot}`, {
    method: "POST",
    credentials: "include",
    body: formData,
  });

  if (res.status === 401) {
    window.location.href = `/api/auth/login?returnTo=${encodeURIComponent(window.location.pathname)}`;
    throw new ApiError(401, "AUTH_REQUIRED", "Not authenticated");
  }

  if (!res.ok) {
    const body = await res.json().catch(() => ({ error: res.statusText, code: "UNKNOWN" }));
    throw new ApiError(res.status, body.code ?? "UNKNOWN", body.error ?? "Request failed", body.details);
  }

  return res.json() as Promise<ImagerySlotData>;
}

export function useImagerySlots(recordId: string | undefined) {
  return useQuery({
    queryKey: slotsQueryKey(recordId),
    queryFn: () => api.get<ImagerySlots>(`/api/insurance/${recordId}/images/slots`),
    enabled: !!recordId,
  });
}

export function useUploadSlotImage(recordId: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ slot, file }: { slot: ImagerySlotName; file: File }) => {
      if (!recordId) throw new ApiError(400, "NO_RECORD", "Record must be saved first");
      return uploadSlotFile(recordId, slot, file);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: slotsQueryKey(recordId) });
    },
  });
}

// Fire-and-forget request to warm the server-side imagery/map cache for a
// coordinate pair. Errors are swallowed since this is a best-effort
// pre-warm; the normal options query still renders if warming fails.
export function warmImageryCache(lat: number, lng: number): void {
  api.post("/api/imagery/warm", { lat, lng }).catch(() => {
    // Warming is best-effort; the options query will render on demand anyway.
  });
}

export function useImageryOptionsByCoords(lat: number | null | undefined, lng: number | null | undefined) {
  const hasCoords = lat != null && lng != null;
  const roundedLat = hasCoords ? roundCoord(lat) : null;
  const roundedLng = hasCoords ? roundCoord(lng) : null;

  return useQuery({
    queryKey: imageryOptionsByCoordsQueryKey(roundedLat, roundedLng),
    queryFn: () => api.get<ImageryOptionsResult>(`/api/imagery/options?lat=${roundedLat}&lng=${roundedLng}`),
    enabled: hasCoords,
    staleTime: IMAGERY_OPTIONS_STALE_MS,
    retry: false,
  });
}

export interface ImportImageryPayload {
  kind: ImageryOptionKind;
  target: ImageryImportTarget;
  caption?: string;
  lat?: number;
  lng?: number;
}

export function useImportImagery(recordId: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: ImportImageryPayload) =>
      api.post<{ ok: boolean; target: ImageryImportTarget; key: string }>(
        `/api/insurance/${recordId}/imagery/import`,
        body,
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: slotsQueryKey(recordId) });
      queryClient.invalidateQueries({ queryKey: ["insurance", recordId, "photos"] });
    },
  });
}

export function useMoveImage(recordId: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: MoveImagePayload) => api.post<MoveImageResult>(`/api/insurance/${recordId}/images/move`, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: slotsQueryKey(recordId) });
      queryClient.invalidateQueries({ queryKey: ["insurance", recordId, "photos"] });
    },
  });
}
